/**
 * OAuth Service
 *
 * OAuth2.0ソーシャルログインのAPI通信を担当するサービスモジュール
 * Google / Microsoft / GitHub の認証フローをサポート
 */

import axios from 'axios';
import type { AxiosError } from 'axios';
import type { OAuthProvider, OAuthProviderInfo, SaaSUser } from '../types';
import apiService from './api';

// ========== Configuration ==========

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const API_BASE = `${API_BASE_URL}/api/auth/oauth`;

// ========== Types ==========

export interface OAuthLoginResponse {
  user: SaaSUser;
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface OAuthLinkRequiredDetail {
  code: 'oauth_link_required';
  provider: string;
  email: string;
  existing_methods: { password: boolean; providers: string[] };
  link_intent_token: string;
}

export class OAuthLinkRequiredError extends Error {
  readonly detail: OAuthLinkRequiredDetail;
  constructor(detail: OAuthLinkRequiredDetail) {
    super('oauth_link_required');
    this.name = 'OAuthLinkRequiredError';
    this.detail = detail;
  }
}

export interface OAuthAccount {
  provider: string;
  // BE は Optional[str]（DB カラム nullable）。null 許容で契約を一致させる
  provider_email: string | null;
  linked_at: string;
  last_used_at: string | null;
}

// ========== API Client Instance ==========

const oauthApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

oauthApiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const data = error.response?.data as Record<string, unknown> | undefined;
    if (
      error.response?.status === 409 &&
      (data?.detail as { code?: string } | undefined)?.code === 'oauth_link_required'
    ) {
      return Promise.reject(
        new OAuthLinkRequiredError(data!.detail as OAuthLinkRequiredDetail)
      );
    }
    const message =
      (typeof data?.detail === 'string' ? data.detail : undefined) ||
      error.message ||
      '認証に失敗しました';
    return Promise.reject(new Error(message));
  }
);

// ========== OAuth Service ==========

export const oauthService = {
  /**
   * OAuth認可フローを開始（ページリダイレクト方式）
   * startOAuthFlow 呼び出し前に provider を sessionStorage に保存しておく
   */
  startOAuthFlow(provider: OAuthProvider): void {
    // 通常ログイン開始時は連携モードの残骸を必ず消す
    // （設定画面連携をキャンセルした直後の通常ログインが
    //  完了 callback に誤って入るのを防ぐ二重防御）
    sessionStorage.removeItem('oauth_link_mode');
    sessionStorage.setItem('oauth_provider', provider);
    window.location.href = `${API_BASE}/${provider}/authorize`;
  },

  /**
   * OAuthコールバック処理（BEにcode+stateを送信してJWTを取得）
   */
  async handleOAuthCallback(
    code: string,
    state: string,
    provider: OAuthProvider
  ): Promise<OAuthLoginResponse> {
    const response = await oauthApiClient.post<OAuthLoginResponse>(
      '/api/auth/oauth/callback',
      { code, state, provider }
    );
    return response.data;
  },

  /**
   * 利用可能なOAuthプロバイダー一覧を取得
   *
   * エラーは呼び出し元（TanStack Query）に伝播させる。
   * catch で [] を返す設計は廃止: isError が立たず useOAuthProviders の
   * FALLBACK_PROVIDERS 分岐（耐障害挙動）が死ぬため。
   * - 成功時: providers 配列を resolve（available フィルタは hook 層で行う）
   * - 失敗時: reject → query.isError=true → hook が FALLBACK_PROVIDERS を返す
   */
  async getAvailableProviders(): Promise<OAuthProviderInfo[]> {
    const response = await oauthApiClient.get<{ providers: OAuthProviderInfo[] }>(
      '/api/auth/oauth/providers'
    );
    return response.data.providers;
  },

  /**
   * link_intent_token を使ってアカウント連携を完了する
   * POST /api/auth/oauth/link/complete（認証必須）
   */
  async completeLink(token: string): Promise<{ success: boolean }> {
    const authToken = apiService.getToken();
    const response = await oauthApiClient.post<{ success: boolean }>(
      '/api/auth/oauth/link/complete',
      { link_intent_token: token },
      authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {}
    );
    return response.data;
  },

  /**
   * 連携済みOAuthアカウント一覧を取得
   * GET /api/auth/oauth/accounts（認証必須）
   */
  async listOAuthAccounts(): Promise<{ accounts: OAuthAccount[]; has_password: boolean }> {
    const authToken = apiService.getToken();
    const response = await oauthApiClient.get<{ accounts: OAuthAccount[]; has_password: boolean }>(
      '/api/auth/oauth/accounts',
      authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {}
    );
    return response.data;
  },

  /**
   * OAuthアカウント連携を解除する
   * DELETE /api/auth/oauth/accounts/{provider}（認証必須）
   */
  async unlinkOAuthAccount(provider: string): Promise<void> {
    const authToken = apiService.getToken();
    await oauthApiClient.delete(
      `/api/auth/oauth/accounts/${provider}`,
      authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {}
    );
  },

  /**
   * 設定画面からの新規連携フローを開始（ページリダイレクト）
   * sessionStorage に oauth_link_mode=true を設定してから /authorize へリダイレクト
   */
  startLinkFlow(provider: OAuthProvider): void {
    sessionStorage.setItem('oauth_link_mode', 'true');
    sessionStorage.setItem('oauth_provider', provider);
    window.location.href = `${API_BASE}/${provider}/authorize`;
  },

  /**
   * 設定画面からの連携コールバック処理
   * POST /api/auth/oauth/link（認証必須）
   */
  async completeLinkCallback(
    code: string,
    state: string,
    provider: OAuthProvider
  ): Promise<{ success: boolean }> {
    const authToken = apiService.getToken();
    const response = await oauthApiClient.post<{ success: boolean }>(
      '/api/auth/oauth/link',
      { code, state, provider },
      authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {}
    );
    return response.data;
  },
};
